import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Loader2, Globe2, Cpu, Building2, ArrowRight, Mic2, FileText, Radio, Bell, BellOff } from "lucide-react";
import { loadPrefs, savePrefs } from "@/lib/storage";
import { detectLocation } from "@/lib/geo";
import { generateCapsule } from "@/lib/api";
import { primeAudioOnGesture } from "@/lib/chime";
import RecentCapsules from "@/components/RecentCapsules";
import BuildBadge from "@/components/BuildBadge";
import FeedbackWidget from "@/components/FeedbackWidget";
import SearchBar from "@/components/SearchBar";
import InterestsInput from "@/components/InterestsInput";

const DURATIONS = [10];

const CUTOFF_BY_MODE = {
  text: { 10: "1.5 min" },
  audio: { 10: "4 min" },
};

const LANGUAGES = [
  { id: "en", label: "English" },
  { id: "hi", label: "हिन्दी (Hindi)" },
];

const TECH_TOPICS = [
  "Artificial Intelligence",
  "Electronics",
  "LEDs & Displays",
  "Semiconductors",
  "Robotics",
  "Biotech",
  "Space",
  "Environment & Climate",
  "Energy & Clean Tech",
  "Cybersecurity",
  "Quantum Computing",
];

const VOICES = [];  // voice is now derived from language automatically

const Chip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`border px-4 py-2.5 text-sm font-medium uppercase tracking-wider transition-colors ${
      active
        ? "border-zinc-950 bg-zinc-950 text-white"
        : "border-zinc-200 bg-transparent text-zinc-700 hover:border-zinc-950 hover:text-zinc-950"
    }`}
  >
    {children}
  </button>
);

const SectionLabel = ({ index, children }) => (
  <div className="mb-4 flex items-baseline gap-3">
    <span className="font-mono text-xs uppercase tracking-[0.3em] text-rose-600">
      {String(index).padStart(2, "0")}
    </span>
    <h2 className="text-xs font-semibold uppercase tracking-[0.25em] text-zinc-500">
      {children}
    </h2>
  </div>
);

export default function Onboarding() {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(() => {
    const p = loadPrefs();
    p.duration_minutes = 10;
    if (!["text", "audio"].includes(p.mode)) p.mode = "audio";
    // Migrate legacy voice names to Gemini voices
    const validVoices = VOICES.map((v) => v.id);
    if (!validVoices.includes(p.voice)) p.voice = "Kore";
    if (!Array.isArray(p.interests)) p.interests = [];
    return p;
  });
  const [detectingLoc, setDetectingLoc] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric", year: "numeric"
  });

  useEffect(() => { savePrefs(prefs); }, [prefs]);

  const detect = async () => {
    setDetectingLoc(true);
    try {
      const loc = await detectLocation();
      // Country-level scope only; discard city/region noise.
      const trimmed = { country: loc.country || "", latitude: loc.latitude, longitude: loc.longitude };
      setPrefs((p) => ({ ...p, location: trimmed, include_local: true }));
      toast.success(`Detected: ${trimmed.country || "your country"}`);
      return trimmed;
    } catch {
      toast.error("Couldn't detect location. Enter your country manually below.");
      return null;
    } finally {
      setDetectingLoc(false);
    }
  };

  const hasLocation = (loc) => !!(loc && loc.country);

  const onToggleLocal = async () => {
    const next = !prefs.include_local;
    setPrefs((p) => ({ ...p, include_local: next }));
    if (next && !hasLocation(prefs.location)) {
      // Auto-detect on first activation so users don't get silent drops.
      await detect();
    }
  };

  const onToggleTech = () => {
    setPrefs((p) => {
      const next = !p.include_tech;
      // If enabling tech with no topics yet, seed a sensible default so
      // the section actually gets requested from the LLM.
      const seed = next && p.tech_topics.length === 0
        ? ["Artificial Intelligence"]
        : p.tech_topics;
      return { ...p, include_tech: next, tech_topics: seed };
    });
  };

  const setLocField = (field, value) => {
    setPrefs((p) => ({
      ...p,
      location: { ...(p.location || {}), [field]: value },
      include_local: true,
    }));
  };

  const toggleTechTopic = (t) => {
    setPrefs((p) => {
      const has = p.tech_topics.includes(t);
      return {
        ...p,
        include_tech: has ? p.tech_topics.length > 1 : true,
        tech_topics: has ? p.tech_topics.filter((x) => x !== t) : [...p.tech_topics, t],
      };
    });
  };

  const toggleNotify = async () => {
    if (prefs.notify_enabled) {
      setPrefs((p) => ({ ...p, notify_enabled: false }));
      toast.success("Daily reminder turned off.");
      return;
    }
    if (typeof Notification === "undefined") {
      toast.error("Notifications aren't supported in this browser.");
      return;
    }
    let perm = Notification.permission;
    if (perm === "default") {
      try { perm = await Notification.requestPermission(); }
      catch { perm = "denied"; }
    }
    if (perm !== "granted") {
      toast.error("Notification permission was denied. Enable it in your browser settings to receive reminders.");
      return;
    }
    setPrefs((p) => ({ ...p, notify_enabled: true }));
    toast.success(`Daily reminder set for ${prefs.notify_time}.`);
  };

  const setNotifyTime = (value) => {
    setPrefs((p) => ({ ...p, notify_time: value }));
  };

  const canSubmit = prefs.include_global || prefs.include_local || prefs.include_tech;
  const cutoffLabel = CUTOFF_BY_MODE[prefs.mode]?.[prefs.duration_minutes] || "1 min";

  const onSubmit = async () => {
    if (!canSubmit) {
      toast.error("Select at least one topic section.");
      return;
    }
    // Block silent-drop cases with a clear message.
    if (prefs.include_local && !hasLocation(prefs.location)) {
      toast.error("Local is on but you haven't set a country. Auto-detect or type it in.");
      return;
    }
    if (prefs.include_tech && prefs.tech_topics.length === 0) {
      toast.error("Technology is on but no topics are selected. Pick at least one below.");
      return;
    }
    // Unlock audio while we still hold the user gesture — iOS Safari
    // won't let the ready-chime play otherwise.
    primeAudioOnGesture();
    setSubmitting(true);
    try {
      const payload = {
        duration_minutes: 10,
        mode: prefs.mode,
        language: prefs.language || "en",
        location: prefs.location,
        include_global: prefs.include_global,
        include_local: prefs.include_local,
        include_tech: prefs.include_tech,
        tech_topics: prefs.tech_topics,
        interests: (prefs.interests || []).filter(Boolean).slice(0, 15),
        client_hour: new Date().getHours(),
        voice: "Kore",
      };
      const res = await generateCapsule(payload);
      savePrefs({ ...prefs, lastCapsuleId: res.id });
      navigate(`/capsule/${res.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start capsule. Try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 grain">
      <header className="border-b border-zinc-950 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-4 md:px-10">
          <div className="flex items-center gap-3">
            <span className="block h-2.5 w-2.5 animate-pulse bg-rose-600" />
            <span className="hidden font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-600 md:inline">
              On Air · {today}
            </span>
          </div>
          <div className="font-display text-xl font-black tracking-tight md:text-2xl" data-testid="brand-logo">
            THE DAILY CAPSULE
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 border border-zinc-300 p-0.5" data-testid="language-switcher-top">
              {LANGUAGES.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  data-testid={`language-${l.id}-top`}
                  onClick={() => setPrefs((p) => ({ ...p, language: l.id }))}
                  className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${
                    prefs.language === l.id
                      ? "bg-zinc-950 text-white"
                      : "bg-transparent text-zinc-600 hover:text-zinc-950"
                  }`}
                >
                  {l.id === "hi" ? "हिन्दी" : "EN"}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="overflow-hidden border-t border-zinc-200 bg-zinc-50 py-2">
          <motion.div
            className="flex gap-10 whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500"
            initial={{ x: 0 }} animate={{ x: "-50%" }}
            transition={{ repeat: Infinity, duration: 35, ease: "linear" }}
          >
            {Array.from({ length: 12 }).map((_, i) => (
              <span key={`ticker-${i}`} className="flex items-center gap-3">
                Global Markets <span className="text-rose-600">▲</span>
                Local Headlines <span className="text-rose-600">●</span>
                AI Breakthroughs <span className="text-rose-600">◆</span>
                Electronics & LEDs <span className="text-rose-600">◼</span>
              </span>
            ))}
          </motion.div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-12 md:px-10 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="grid grid-cols-1 gap-10 md:grid-cols-12"
        >
          <div className="md:col-span-8">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">
              A bespoke newsroom, in your pocket
            </p>
            <h1 className="mt-4 font-display text-5xl font-black leading-[0.95] tracking-tight text-zinc-950 lg:text-7xl">
              Your news,<br />
              <span className="italic text-rose-600">{prefs.mode === "audio" ? "read aloud" : "written up"}</span><br />
              in {prefs.duration_minutes} minutes.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-600 lg:text-lg">
              Choose your topics and we'll draft{prefs.mode === "audio" ? " and voice" : ""} a daily capsule
              tailored to where you are and what you care about — global, local, and the technology
              you live with. Ready in about <span className="font-semibold text-zinc-950">{cutoffLabel}</span>.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                type="button"
                data-testid="generate-capsule-btn-top"
                disabled={submitting || !canSubmit}
                onClick={onSubmit}
                className="group inline-flex items-center gap-3 bg-rose-600 px-8 py-4 text-sm font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-zinc-950 disabled:cursor-not-allowed disabled:opacity-50 md:text-base"
              >
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                {submitting ? "Starting…" : "Generate capsule"}
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </button>
              <p className="max-w-xs font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                Ready in ~{cutoffLabel} · powered by Gemini
              </p>
            </div>
          </div>
          <div className="md:col-span-4">
            <div className="border border-zinc-950 bg-zinc-950 p-6 text-white">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-rose-400">Now Featuring</p>
              <ul className="mt-4 space-y-3 text-sm leading-relaxed">
                <li className="flex items-start gap-3"><Globe2 className="mt-0.5 h-4 w-4 text-rose-400" />Global affairs and markets</li>
                <li className="flex items-start gap-3"><Building2 className="mt-0.5 h-4 w-4 text-rose-400" />Local stories at country level</li>
                <li className="flex items-start gap-3"><Cpu className="mt-0.5 h-4 w-4 text-rose-400" />Tech breakthroughs in your fields</li>
                <li className="flex items-start gap-3"><Radio className="mt-0.5 h-4 w-4 text-rose-400" />Spoken audio or text</li>
              </ul>
            </div>
          </div>
        </motion.div>

        <section className="mt-16 grid grid-cols-1 gap-10 md:grid-cols-12">
          {/* Mode */}
          <div className="md:col-span-6">
            <SectionLabel index={1}>Delivery mode</SectionLabel>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="mode-audio"
                onClick={() => setPrefs((p) => ({ ...p, mode: "audio" }))}
                className={`flex flex-col items-start gap-3 border p-5 text-left transition-colors ${
                  prefs.mode === "audio"
                    ? "border-zinc-950 bg-zinc-950 text-white"
                    : "border-zinc-200 bg-white hover:border-zinc-950"
                }`}
              >
                <Mic2 className="h-6 w-6" />
                <span className="font-display text-2xl font-bold tracking-tight">Audio</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] opacity-70">Listen · spoken</span>
              </button>
              <button
                type="button"
                data-testid="mode-text"
                onClick={() => setPrefs((p) => ({ ...p, mode: "text" }))}
                className={`flex flex-col items-start gap-3 border p-5 text-left transition-colors ${
                  prefs.mode === "text"
                    ? "border-zinc-950 bg-zinc-950 text-white"
                    : "border-zinc-200 bg-white hover:border-zinc-950"
                }`}
              >
                <FileText className="h-6 w-6" />
                <span className="font-display text-2xl font-bold tracking-tight">Text</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] opacity-70">Read · fastest</span>
              </button>
            </div>
          </div>

          {/* Duration */}
          <div className="md:col-span-6">
            <SectionLabel index={2}>Capsule duration</SectionLabel>
            <div className="border border-zinc-200 bg-white p-6">
              <div className="flex items-baseline justify-between">
                <span className="font-display text-6xl font-black tracking-tight text-zinc-950 lg:text-7xl">
                  {prefs.duration_minutes}
                </span>
                <span className="font-mono text-sm uppercase tracking-[0.3em] text-zinc-500">minutes</span>
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {DURATIONS.map((d) => (
                  <Chip
                    key={d}
                    testId={`duration-${d}`}
                    active={prefs.duration_minutes === d}
                    onClick={() => setPrefs((p) => ({ ...p, duration_minutes: d }))}
                  >
                    {d} min
                  </Chip>
                ))}
              </div>
              <p className="mt-4 text-sm text-zinc-500">
                Cutoff: <span className="font-semibold text-zinc-950">{cutoffLabel}</span>. If it takes longer, the capsule fails cleanly so you're never stuck waiting.
              </p>
            </div>
          </div>

          {/* Language */}
          <div className="md:col-span-12">
            <SectionLabel index={3}>Language</SectionLabel>
            <div className="border border-zinc-200 bg-white p-6">
              <div className="flex flex-wrap gap-2" data-testid="language-switcher">
                {LANGUAGES.map((l) => (
                  <Chip
                    key={l.id}
                    testId={`language-${l.id}`}
                    active={prefs.language === l.id}
                    onClick={() => setPrefs((p) => ({ ...p, language: l.id }))}
                  >
                    {l.label}
                  </Chip>
                ))}
              </div>
              <p className="mt-4 text-sm text-zinc-500">
                {prefs.language === "hi"
                  ? "Script and audio (if selected) will be in Hindi."
                  : "Script and audio (if selected) will be in English."}
              </p>
            </div>
          </div>

          {/* Daily Reminder */}
          <div className="md:col-span-12">
            <SectionLabel index={4}>Daily reminder</SectionLabel>
            <div className="border border-zinc-200 bg-white p-6">
              <div className="flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  data-testid="toggle-notify"
                  onClick={toggleNotify}
                  className={`inline-flex items-center gap-2 border px-5 py-3 text-sm font-medium uppercase tracking-wider transition-colors ${
                    prefs.notify_enabled
                      ? "border-zinc-950 bg-zinc-950 text-white hover:bg-rose-600 hover:border-rose-600"
                      : "border-zinc-950 bg-transparent text-zinc-950 hover:bg-zinc-950 hover:text-white"
                  }`}
                >
                  {prefs.notify_enabled ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
                  {prefs.notify_enabled ? "Reminder On" : "Enable reminder"}
                </button>
                <label className="flex items-center gap-3">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">At</span>
                  <input
                    type="time"
                    data-testid="notify-time-input"
                    value={prefs.notify_time}
                    onChange={(e) => setNotifyTime(e.target.value)}
                    className="border border-zinc-300 bg-white px-3 py-2.5 font-mono text-sm outline-none focus:border-zinc-950"
                  />
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">local time</span>
                </label>
              </div>
              <p className="mt-4 max-w-2xl text-sm text-zinc-500">
                We'll ping you daily at your chosen time so you never miss the brew. Default is 8:00 AM.
                Reminders fire while the app is open in a tab or installed as an app on your device.
              </p>
              <label className="mt-5 flex items-center gap-3 text-sm text-zinc-700">
                <input
                  type="checkbox"
                  data-testid="ready-chime-toggle"
                  checked={prefs.ready_chime_enabled !== false}
                  onChange={(e) => setPrefs((p) => ({ ...p, ready_chime_enabled: e.target.checked }))}
                  className="h-4 w-4 accent-rose-600"
                />
                Play a soft chime{prefs.mode === "audio" ? " and announce" : ""} when a capsule finishes brewing
              </label>
            </div>
          </div>

          {/* Interests — free-form personal topics */}
          <div className="md:col-span-12">
            <SectionLabel index={5}>Your interests</SectionLabel>
            <div className="border border-zinc-200 bg-white p-6">
              <p className="text-sm text-zinc-500">
                Tell us the topics you actually care about so we prioritise them across every section.
                Add up to 15 — e.g. <span className="italic">cybersecurity, cricket, F1, EV, semiconductors, Indian politics</span>.
              </p>
              <div className="mt-5">
                <InterestsInput
                  value={prefs.interests || []}
                  onChange={(next) => setPrefs((p) => ({ ...p, interests: next }))}
                />
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="md:col-span-12">
            <SectionLabel index={6}>Sections</SectionLabel>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <button
                type="button"
                data-testid="toggle-global"
                onClick={() => setPrefs((p) => ({ ...p, include_global: !p.include_global }))}
                className={`flex flex-col gap-4 border p-6 text-left transition-colors ${
                  prefs.include_global ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white hover:border-zinc-950"
                }`}
              >
                <Globe2 className="h-7 w-7" />
                <div>
                  <p className="font-display text-2xl font-bold tracking-tight">Global</p>
                  <p className="mt-1 text-sm opacity-80">International affairs, economy, diplomacy.</p>
                </div>
                <span className={`mt-2 font-mono text-[11px] uppercase tracking-[0.3em] ${prefs.include_global ? "text-rose-400" : "text-zinc-500"}`}>
                  {prefs.include_global ? "Included" : "Tap to include"}
                </span>
              </button>

              <button
                type="button"
                data-testid="toggle-local"
                onClick={onToggleLocal}
                className={`flex flex-col gap-4 border p-6 text-left transition-colors ${
                  prefs.include_local ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white hover:border-zinc-950"
                }`}
              >
                <Building2 className="h-7 w-7" />
                <div>
                  <p className="font-display text-2xl font-bold tracking-tight">Local</p>
                  <p className="mt-1 text-sm opacity-80">National headlines for your country.</p>
                </div>
                <span className={`mt-2 font-mono text-[11px] uppercase tracking-[0.3em] ${prefs.include_local ? "text-rose-400" : "text-zinc-500"}`}>
                  {prefs.include_local ? "Included" : "Tap to include"}
                </span>
              </button>

              <button
                type="button"
                data-testid="toggle-tech"
                onClick={onToggleTech}
                className={`flex flex-col gap-4 border p-6 text-left transition-colors ${
                  prefs.include_tech ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white hover:border-zinc-950"
                }`}
              >
                <Cpu className="h-7 w-7" />
                <div>
                  <p className="font-display text-2xl font-bold tracking-tight">Technology</p>
                  <p className="mt-1 text-sm opacity-80">Breakthroughs in your fields.</p>
                </div>
                <span className={`mt-2 font-mono text-[11px] uppercase tracking-[0.3em] ${prefs.include_tech ? "text-rose-400" : "text-zinc-500"}`}>
                  {prefs.include_tech ? "Included" : "Tap to include"}
                </span>
              </button>
            </div>
          </div>

          {prefs.include_local && (
            <div className="md:col-span-7">
              <SectionLabel index={7}>Your country</SectionLabel>
              <div className="border border-zinc-200 bg-white p-6">
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    data-testid="detect-location-btn"
                    onClick={detect}
                    disabled={detectingLoc}
                    className="inline-flex items-center gap-2 bg-zinc-950 px-5 py-3 text-sm font-medium uppercase tracking-wider text-white hover:bg-rose-600 transition-colors disabled:opacity-60"
                  >
                    {detectingLoc ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
                    {detectingLoc ? "Detecting…" : "Auto-detect"}
                  </button>
                  <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">or enter manually</span>
                </div>
                <div className="mt-6">
                  <label className="flex flex-col gap-1">
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Country</span>
                    <input
                      data-testid="location-country"
                      value={prefs.location?.country || ""}
                      onChange={(e) => setLocField("country", e.target.value)}
                      className="border border-zinc-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-zinc-950"
                      placeholder="India, United States, United Kingdom…"
                    />
                  </label>
                  <p className="mt-3 text-xs text-zinc-500">
                    Local news is scoped country-wide (politics, economy, sport, culture) — no city or state noise.
                  </p>
                </div>
              </div>
            </div>
          )}

          {prefs.include_tech && (
            <div className={prefs.include_local ? "md:col-span-5" : "md:col-span-12"}>
              <SectionLabel index={prefs.include_local ? 8 : 7}>
                Technology topics
              </SectionLabel>
              <div className="border border-zinc-200 bg-white p-6">
                <p className="mb-4 text-sm text-zinc-500">
                  Pick the fields you follow. We'll prioritise breakthroughs there.
                </p>
                <div className="flex flex-wrap gap-2">
                  {TECH_TOPICS.map((t) => (
                    <Chip
                      key={t}
                      testId={`tech-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      active={prefs.tech_topics.includes(t)}
                      onClick={() => toggleTechTopic(t)}
                    >
                      {t}
                    </Chip>
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>

        <div className="mt-16 flex flex-col items-start justify-between gap-6 border-t border-zinc-950 pt-10 md:flex-row md:items-end">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">Ready</p>
            <p className="mt-2 font-display text-3xl font-bold tracking-tight text-zinc-950 md:text-4xl">
              Brew today's capsule
            </p>
            <p className="mt-2 max-w-md text-sm text-zinc-500">
              Live progress meter · hard cutoff at {cutoffLabel} · powered by Gemini.
            </p>
          </div>
          <button
            type="button"
            data-testid="generate-capsule-btn"
            disabled={submitting || !canSubmit}
            onClick={onSubmit}
            className="group inline-flex items-center gap-3 bg-rose-600 px-10 py-5 text-sm font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-zinc-950 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {submitting ? "Starting…" : "Generate capsule"}
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </button>
        </div>

        <SearchBar />
        <RecentCapsules />
      </main>

      <footer className="mt-12 border-t border-zinc-200">
        <div className="mx-auto flex max-w-7xl flex-col justify-between gap-2 px-6 py-6 text-xs text-zinc-500 md:flex-row md:items-center md:px-10">
          <span className="font-mono uppercase tracking-[0.3em]">The Daily Capsule · v2 · Powered by Gemini</span>
          <BuildBadge />
          <span>AI-written{prefs.mode === "audio" ? ", AI-voiced" : ""}. Verify important facts with primary sources.</span>
        </div>
      </footer>

      <FeedbackWidget context="onboarding" />
    </div>
  );
}
